import React from 'react';
import { useNavigate } from 'react-router-dom';

const INTRO_VIDEO_URL =
  'https://customer-assets.emergentagent.com/job_rpg-chatroom/artifacts/ydjeqjvj_Mandala_Intro_01.mp4';

// Session storage flag — once the intro has played this session, /intro
// redirects straight to the login so users aren't force-sat through it every
// nav.  Users can still hit /intro directly (or clear session storage) to
// replay it.
const SESSION_SEEN_KEY = 'mandala_intro_seen';

export default function IntroVideo() {
  const navigate = useNavigate();
  const videoRef = React.useRef(null);
  const [ended, setEnded] = React.useState(false);
  const [muted, setMuted] = React.useState(false);

  React.useEffect(() => {
    // If the user already watched the intro this session, skip straight to login.
    try {
      if (sessionStorage.getItem(SESSION_SEEN_KEY) === '1') {
        navigate('/login', { replace: true });
        return;
      }
    } catch { /* ignore storage errors */ }

    // Msg #801 — User wants audio ON by default. Try to autoplay with sound;
    // if the browser autoplay policy blocks it, do NOT silently fall back to
    // muted. The user can still hit the Mute pill themselves if they want to.
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      v.volume = 1.0;
      v.play().catch(() => { /* browser blocked audio autoplay — leave paused */ });
    }
  }, [navigate]);

  const goToLogin = React.useCallback(() => {
    if (ended) return;
    setEnded(true);
    try { sessionStorage.setItem(SESSION_SEEN_KEY, '1'); } catch { /* ignore */ }
    navigate('/login', { replace: true });
  }, [ended, navigate]);

  const toggleMute = React.useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.volume = 1.0;
    setMuted(v.muted);
    // Kick playback in case autoplay-with-audio was blocked earlier.
    if (v.paused) v.play().catch(() => { /* ignore */ });
  }, []);

  return (
    <div
      data-testid="intro-video-page"
      onClick={goToLogin}
      style={{
        position: 'fixed', inset: 0,
        background: '#000',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', zIndex: 10000,
      }}
    >
      <video
        ref={videoRef}
        data-testid="intro-video-player"
        src={INTRO_VIDEO_URL}
        autoPlay
        playsInline
        preload="auto"
        onEnded={goToLogin}
        onError={() => { /* keep overlay + skip pill so the user can proceed */ }}
        style={{
          width: '100%', height: '100%',
          objectFit: 'contain',
          background: '#000',
        }}
      />
      <div
        style={{
          position: 'absolute', bottom: 24, right: 32,
          display: 'flex', gap: 12, alignItems: 'center',
          fontFamily: 'Georgia, serif',
          fontSize: 14, letterSpacing: 2, textTransform: 'uppercase',
          textShadow: '0 1px 4px rgba(0,0,0,0.9)',
          userSelect: 'none',
        }}
      >
        <button
          data-testid="intro-video-mute"
          onClick={toggleMute}
          title={muted ? 'Unmute' : 'Mute'}
          style={{
            background: 'rgba(20,20,20,0.55)',
            color: '#f5e6c0',
            border: '1px solid #c8a94d',
            padding: '6px 14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 14, letterSpacing: 2, textTransform: 'uppercase',
          }}
        >
          {muted ? '🔇 Unmute' : '🔊 Mute'}
        </button>
        <div
          data-testid="intro-video-skip"
          style={{
            color: '#f5e6c0',
            background: 'rgba(20,20,20,0.55)',
            border: '1px solid #c8a94d',
            padding: '6px 16px',
          }}
        >
          ✕ Skip Intro
        </div>
      </div>
    </div>
  );
}
